import React, { useEffect, createElement } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ShieldCheck, PenTool, Building, Wind, MapPin } from 'lucide-react';
// Content database for flagship cities to ensure unique, high-quality content
const cityData: Record<string, any> = {
  birmingham: {
    name: 'Birmingham',
    buildings: 'city-centre office blocks, retail units in the Bullring, and extensive industrial estates around Aston and Erdington',
    projects: ['Office fit-outs in city-centre blocks near Snow Hill and New Street.', 'VRV/VRF systems for multi-storey offices in Brindleyplace.', 'AC and ventilation upgrades in warehouses and production units around Aston / Erdington industrial estates.', 'Comfort cooling for retail units in major shopping streets and retail parks.', 'TM44 inspections and maintenance contracts for multi-site landlords across the wider Birmingham area.'],
    caseStudy: {
      title: 'Warehouse cooling upgrade in Erdington',
      situation: 'An industrial facility struggling with outdated systems, uncomfortable staff temperatures, and escalating energy costs.',
      solution: 'Design and installation of a new Daikin VRV system with intelligent zoning and advanced controls.',
      outcome: 'Stable temperatures across the production floor, significantly lower utility bills, and improved staff productivity.'
    }
  },
  coventry: {
    name: 'Coventry',
    buildings: 'manufacturing facilities, logistics hubs near the M6, and modern office developments in the city centre',
    projects: ['Large-scale ventilation and cooling for manufacturing plants in the Coventry area.', 'Energy-efficient AC installations for logistics hubs and distribution centres near the M6.', 'Server room cooling solutions for technology firms and corporate offices.', 'Comprehensive maintenance contracts for educational facilities and healthcare sites.', 'TM44 compliance audits for commercial landlords across Coventry and Warwickshire.'],
    caseStudy: {
      title: 'Manufacturing facility ventilation in Coventry',
      situation: 'A production site requiring strict temperature control and improved air quality for sensitive manufacturing processes.',
      solution: 'Custom-designed ventilation and cooling system integrated with existing infrastructure.',
      outcome: 'Consistent environmental conditions, full regulatory compliance, and reduced system downtime.'
    }
  },
  solihull: {
    name: 'Solihull',
    buildings: 'premium business parks, corporate headquarters, and high-end retail spaces',
    projects: ['Discreet ducted AC systems for premium office spaces in Solihull business parks.', 'Retail cooling solutions ensuring customer comfort in high-footfall areas.', 'Energy-efficient upgrades for corporate headquarters seeking to reduce their carbon footprint.', 'Routine maintenance and rapid-response repairs for local FM companies.', 'Air quality and ventilation improvements for hospitality venues.'],
    caseStudy: {
      title: 'Corporate office HVAC upgrade in Solihull',
      situation: 'A multi-tenant office building with aging, inefficient cooling systems causing tenant complaints.',
      solution: 'Phased replacement with modern, energy-efficient Mitsubishi Electric systems, minimizing disruption.',
      outcome: 'Elimination of tenant complaints, 30% reduction in energy usage, and improved EPC ratings.'
    }
  },
  wolverhampton: {
    name: 'Wolverhampton',
    buildings: 'industrial units, commercial offices, and retail parks across the Black Country',
    projects: ['Robust HVAC solutions for industrial and manufacturing units in Wolverhampton.', 'Office cooling installations designed for optimal zoning and energy efficiency.', 'Ventilation systems for commercial kitchens and hospitality venues.', 'Preventative maintenance programs for multi-site retail operators.', 'TM44 inspections to ensure compliance for large commercial properties.'],
    caseStudy: {
      title: 'Industrial unit cooling in Wolverhampton',
      situation: 'A manufacturing facility experiencing equipment overheating during summer months.',
      solution: 'Targeted cooling solutions for critical areas and improved general ventilation.',
      outcome: 'Zero equipment failures due to heat, improved worker comfort, and reliable operations.'
    }
  },
  'stoke-on-trent': {
    name: 'Stoke-on-Trent',
    buildings: 'manufacturing sites, distribution centres, and commercial premises',
    projects: ['Specialized ventilation and cooling for manufacturing and ceramics facilities.', 'Large-scale HVAC installations for distribution centres along the A500 corridor.', 'Office air conditioning upgrades for local businesses and corporate branches.', 'Comprehensive maintenance services for healthcare and educational institutions.', 'TM44 energy assessments for commercial landlords in Staffordshire.'],
    caseStudy: {
      title: 'Distribution centre HVAC in Stoke-on-Trent',
      situation: 'A large logistics facility needing efficient heating and cooling across varied zones.',
      solution: 'Installation of high-efficiency heat recovery systems tailored to different operational areas.',
      outcome: 'Optimized energy use, comfortable working conditions in both office and warehouse zones.'
    }
  },
  lichfield: {
    name: 'Lichfield',
    buildings: 'business parks, historic commercial properties, and modern office developments',
    projects: ['Sympathetic AC installations for commercial businesses in historic buildings.', 'Modern, efficient cooling systems for new office developments and business parks.', 'Retail and hospitality ventilation solutions ensuring compliance and comfort.', 'Tailored maintenance contracts for local property management companies.', 'Energy efficiency audits and TM44 inspections.'],
    caseStudy: {
      title: 'Historic building office cooling in Lichfield',
      situation: 'A commercial tenant in a listed building requiring cooling without altering the building fabric.',
      solution: 'Carefully designed and installed discreet systems meeting all conservation requirements.',
      outcome: "Modern comfort levels achieved while preserving the building's historic integrity."
    }
  },
  'royal-leamington-spa': {
    name: 'Royal Leamington Spa',
    buildings: 'technology hubs, gaming studios, and professional service offices',
    projects: ['High-performance cooling for server rooms and technology hubs.', 'Comfort cooling for gaming studios and creative agencies requiring quiet operation.', 'HVAC solutions for professional service offices and corporate spaces.', 'Retail air conditioning for boutique shops and high street stores.', 'Regular maintenance to ensure peak performance and longevity of systems.'],
    caseStudy: {
      title: 'Tech studio cooling in Leamington Spa',
      situation: 'A growing gaming studio facing heat issues from high-performance computing equipment.',
      solution: 'Dedicated server room cooling combined with quiet, draft-free office air conditioning.',
      outcome: 'Protected IT infrastructure and a comfortable, productive environment for developers.'
    }
  },
  'stratford-upon-avon': {
    name: 'Stratford-upon-Avon',
    buildings: 'hospitality venues, retail spaces, and commercial offices',
    projects: ['Ventilation and AC for hotels, restaurants, and hospitality venues.', 'Retail cooling solutions that enhance the customer experience.', 'Office air conditioning installations for local businesses.', 'Maintenance services tailored to the seasonal demands of the tourism industry.', 'Compliance support including TM44 inspections and F-Gas checks.'],
    caseStudy: {
      title: 'Hospitality venue HVAC in Stratford-upon-Avon',
      situation: 'A busy restaurant requiring improved kitchen ventilation and dining area cooling.',
      solution: 'Upgraded extraction systems and discreet cassette AC units in the dining area.',
      outcome: 'Compliant kitchen environment and consistently comfortable dining experience for guests.'
    }
  },
  stafford: {
    name: 'Stafford',
    buildings: 'business parks, logistics centres, and commercial properties',
    projects: ["HVAC installations for businesses in Stafford's commercial parks.", 'Cooling and ventilation for logistics and distribution centres near the M6.', 'Office air conditioning upgrades focusing on energy efficiency.', 'Comprehensive maintenance contracts for M&E contractors and FM companies.', 'TM44 inspections to ensure regulatory compliance.'],
    caseStudy: {
      title: 'Business park office AC in Stafford',
      situation: 'A multi-story office building requiring a complete HVAC overhaul.',
      solution: 'Design and installation of a modern VRF system with individual zone control.',
      outcome: 'Enhanced tenant satisfaction, lower running costs, and improved environmental performance.'
    }
  },
  'burton-upon-trent': {
    name: 'Burton upon Trent',
    buildings: 'manufacturing sites, breweries, and commercial offices',
    projects: ['Specialized cooling and ventilation for manufacturing and brewing facilities.', 'Industrial HVAC solutions for warehouses and production sites.', 'Office air conditioning installations for local and regional businesses.', 'Proactive maintenance programs to minimize downtime in critical environments.', 'TM44 energy assessments and compliance reporting.'],
    caseStudy: {
      title: 'Manufacturing site cooling in Burton upon Trent',
      situation: 'A production facility needing reliable cooling for specific manufacturing processes.',
      solution: 'Robust, industrial-grade cooling systems designed for continuous operation.',
      outcome: 'Consistent production quality, reduced equipment stress, and reliable performance.'
    }
  }
};
// Fallback data for any other city
const getCityData = (slug: string) => {
  if (cityData[slug]) return cityData[slug];
  const formattedName = slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  return {
    name: formattedName,
    buildings: 'offices, retail units, industrial estates, and commercial properties',
    projects: [`Office fit-outs and HVAC upgrades in ${formattedName} commercial centres.`, `VRV/VRF systems for multi-storey buildings and business parks.`, `AC and ventilation upgrades in local warehouses and production units.`, `Comfort cooling for retail units and hospitality venues.`, `TM44 inspections and maintenance contracts for multi-site landlords across the ${formattedName} area.`],
    caseStudy: {
      title: `Commercial cooling upgrade in ${formattedName}`,
      situation: 'A commercial facility struggling with outdated systems and high energy costs.',
      solution: 'Design and installation of a new, energy-efficient HVAC system with intelligent zoning.',
      outcome: 'Stable temperatures, significantly lower utility bills, and improved staff productivity.'
    }
  };
};
export function LocationPage() {
  const {
    city
  } = useParams<{
    city: string;
  }>();
  const data = getCityData(city || 'birmingham');
  // Set meta description dynamically
  useEffect(() => {
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = `Commercial Air Conditioning Install & Maintenance in ${data.name}. Expert design-led HVAC solutions, TM44 inspections, and manufacturer-level support for B2B clients.`;
    document.title = `Commercial Air Conditioning Install & Maintenance in ${data.name} | AccuTemp`;
  }, [data.name]);
  return <main className="min-h-screen font-sans bg-white pt-28" data-id="element-1153">
      {/* Hero Section */}
      <section className="bg-primary-dark text-white py-20 relative overflow-hidden" data-id="element-1154">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-accent/5 rounded-bl-full blur-3xl transform translate-x-1/3 -translate-y-1/4 pointer-events-none" data-id="element-1155"></div>
        <div className="container mx-auto px-6 relative z-10" data-id="element-1156">
          <div className="max-w-3xl" data-id="element-1157">
            <div className="flex items-center gap-2 text-accent font-semibold tracking-wider uppercase text-sm mb-4" data-id="element-1158">
              <MapPin className="w-4 h-4" data-id="element-1159" /> Service Area
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight" data-id="element-1160">
              Commercial air conditioning installation & maintenance in{' '}
              {data.name}
            </h1>
            <p className="text-lg text-white/70 leading-relaxed mb-8" data-id="element-1161">
              We provide expert commercial HVAC services for {data.buildings} in{' '}
              {data.name}. We partner exclusively with FM firms, landlords, M&E
              contractors, and direct businesses to deliver design-led,
              manufacturer-level support.
            </p>

            <div className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8" data-id="element-1162">
              <p className="text-sm font-medium text-white/90 mb-4" data-id="element-1163">
                One of the small number of UK HVAC firms with Constructionline
                Gold and manufacturer-level backing.
              </p>
              <div className="flex flex-wrap gap-4 opacity-70 grayscale" data-id="element-1164">
                <img src="https://cdn.magicpatterns.com/uploads/2vVokvhsbE3igxg2bfPash/d1.png" alt="Daikin D1" className="h-6 object-contain" data-id="element-1165" />
                <img src="https://cdn.magicpatterns.com/uploads/u4wNLmkihgfoVY6zFXiVRD/acc-mitsubishi.png" alt="Mitsubishi" className="h-6 object-contain" data-id="element-1166" />
                <img src="https://cdn.magicpatterns.com/uploads/jFm1QpjEmC16VTGoXegaaN/acc-safe.png" alt="SafeContractor" className="h-6 object-contain" data-id="element-1167" />
              </div>
            </div>

            <div className="flex flex-wrap gap-4" data-id="element-1168">
              <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-bold transition-colors inline-flex items-center gap-2" data-id="element-1169">
                Request a commercial site visit in {data.name}{' '}
                <ArrowRight className="w-5 h-5" data-id="element-1170" />
              </Link>
              <Link to="/contact" className="bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-full font-bold transition-colors" data-id="element-1171">
                Talk to a design engineer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Typical Projects */}
      <section className="py-24 bg-surface/30" data-id="element-1172">
        <div className="container mx-auto px-6" data-id="element-1173">
          <div className="max-w-3xl mb-12" data-id="element-1174">
            <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6" data-id="element-1175">
              Commercial AC projects we deliver in {data.name}
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1176">
              Our design philosophy focuses on comfort, intelligent zoning, air
              quality, and productivity. We don't undertake residential work,
              allowing us to focus entirely on the complex needs of commercial
              environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-1177">
            {data.projects.map((project: string, idx: number) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm border border-primary-dark/5" data-id="element-1178">
                <div className="w-10 h-10 bg-primary-light rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-1179">
                  <CheckCircle className="w-5 h-5 text-accent" data-id="element-1180" />
                </div>
                <p className="text-primary-dark/80 font-medium leading-relaxed" data-id="element-1181">
                  {project}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24" data-id="element-1182">
        <div className="container mx-auto px-6" data-id="element-1183">
          <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-1184">
            <div data-id="element-1185">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-8" data-id="element-1186">
                Why {data.name} businesses work with us
              </h2>
              <div className="space-y-6" data-id="element-1187">
                <div className="flex gap-4" data-id="element-1188">
                  <PenTool className="w-6 h-6 text-accent flex-shrink-0 mt-1" data-id="element-1189" />
                  <div data-id="element-1190">
                    <h3 className="font-bold text-primary-dark text-lg mb-2" data-id="element-1191">
                      Design-led approach
                    </h3>
                    <p className="text-gray-600" data-id="element-1192">
                      Led by ex-manufacturer design engineers. We focus on
                      bespoke zoning for comfort and energy efficiency (e.g.,
                      specifying 10 smaller units instead of one inefficient
                      giant unit).
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-1193">
                  <ShieldCheck className="w-6 h-6 text-accent flex-shrink-0 mt-1" data-id="element-1194" />
                  <div data-id="element-1195">
                    <h3 className="font-bold text-primary-dark text-lg mb-2" data-id="element-1196">
                      Unmatched compliance & safety
                    </h3>
                    <p className="text-gray-600" data-id="element-1197">
                      Constructionline Gold, SafeContractor, REFCOM, and FGAS
                      accredited. Safety and compliance are embedded in
                      everything we do.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-1198">
                  <Building className="w-6 h-6 text-accent flex-shrink-0 mt-1" data-id="element-1199" />
                  <div data-id="element-1200">
                    <h3 className="font-bold text-primary-dark text-lg mb-2" data-id="element-1201">
                      Long-term commercial partnerships
                    </h3>
                    <p className="text-gray-600" data-id="element-1202">
                      We build long-term relationships with FM firms and
                      landlords through multi-year maintenance contracts and
                      manufacturer-level after-sales support.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-primary-dark rounded-3xl p-10 text-white relative overflow-hidden" data-id="element-1203">
              <div className="absolute top-0 right-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" data-id="element-1204"></div>
              <div className="relative z-10" data-id="element-1205">
                <span className="text-accent font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-1206">
                  Local Case Study
                </span>
                <h3 className="text-2xl font-bold mb-6" data-id="element-1207">
                  {data.caseStudy.title}
                </h3>
                <div className="space-y-4 mb-8" data-id="element-1208">
                  <div data-id="element-1209">
                    <strong className="text-white/90 block mb-1" data-id="element-1210">
                      Situation:
                    </strong>
                    <p className="text-white/70 text-sm leading-relaxed" data-id="element-1211">
                      {data.caseStudy.situation}
                    </p>
                  </div>
                  <div data-id="element-1212">
                    <strong className="text-white/90 block mb-1" data-id="element-1213">
                      Solution:
                    </strong>
                    <p className="text-white/70 text-sm leading-relaxed" data-id="element-1214">
                      {data.caseStudy.solution}
                    </p>
                  </div>
                  <div data-id="element-1215">
                    <strong className="text-white/90 block mb-1" data-id="element-1216">
                      Outcome:
                    </strong>
                    <p className="text-white/70 text-sm leading-relaxed" data-id="element-1217">
                      {data.caseStudy.outcome}
                    </p>
                  </div>
                </div>
                <div className="pt-6 border-t border-white/10" data-id="element-1218">
                  <p className="italic text-white/80" data-id="element-1219">
                    "Facilities Manager, commercial site near {data.name}"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Design & Ventilation */}
      <section className="py-24 bg-primary-dark text-white" data-id="element-1220">
        <div className="container mx-auto px-6" data-id="element-1221">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1222">
            <Wind className="w-12 h-12 text-accent mx-auto mb-6" data-id="element-1223" />
            <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-1224">
              Comfort, air quality and compliance in {data.name} workplaces
            </h2>
            <div className="space-y-6 text-lg text-white/80 leading-relaxed text-left mb-12" data-id="element-1225">
              <p data-id="element-1226">
                Our design process is rigorous. We conduct comprehensive
                surveys, heat-load calculations, analyze desk layouts, zoning
                requirements, solar gain, and equipment heat load to specify the
                perfect system for your space.
              </p>
              <p data-id="element-1227">
                Proper ventilation and air changes are critical. We design
                systems that meet specific industry guidelines—whether that's
                standard office air changes, high-extraction requirements for
                commercial kitchens, or specialized environments.
              </p>
              <p data-id="element-1228">
                <strong data-id="element-1229">TM44 Compliance:</strong> Any building with a total
                cooling capacity of 12kW or more requires a TM44 inspection
                every 5 years. We help businesses get and stay compliant through
                audits and reporting, arranged with our trusted partners under
                the AccuTemp umbrella.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/10 inline-block text-left" data-id="element-1230">
              <h3 className="text-xl font-bold mb-2" data-id="element-1231">
                Not sure if your {data.name} building needs a TM44?
              </h3>
              <p className="text-white/70 mb-6" data-id="element-1232">
                Share your system details and we'll confirm for you.
              </p>
              <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-6 py-3 rounded-full font-bold transition-colors inline-flex items-center gap-2" data-id="element-1233">
                Check my compliance status <ArrowRight className="w-4 h-4" data-id="element-1234" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* AEO FAQs */}
      <section className="py-24 bg-surface/50" data-id="element-1235">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-1236">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-12 text-center" data-id="element-1237">
            Frequently asked questions about our services in {data.name}
          </h2>

          <div className="space-y-8" data-id="element-1238">
            <div data-id="element-1239">
              <h3 className="text-xl font-bold text-primary-dark mb-6 border-b border-primary-dark/10 pb-2" data-id="element-1240">
                Service coverage in {data.name}
              </h3>
              <div className="space-y-6" data-id="element-1241">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1242">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1243">
                    Do you offer 24/7 breakdown cover in {data.name}?
                  </h4>
                  <p className="text-gray-600" data-id="element-1244">
                    Yes, we provide 24/7 reactive support for our contracted
                    commercial clients in {data.name}. Our in-house engineers
                    aim for a 4-hour response time to minimize disruption to
                    your business operations.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1245">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1246">
                    Do you install new AC systems in {data.name} offices and
                    warehouses?
                  </h4>
                  <p className="text-gray-600" data-id="element-1247">
                    Yes, we design and install bespoke commercial air
                    conditioning systems across {data.name}. We handle
                    everything from single office units to complex VRF systems
                    for large industrial and commercial spaces.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1248">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1249">
                    Do you provide TM44 AC inspections in {data.name}?
                  </h4>
                  <p className="text-gray-600" data-id="element-1250">
                    Yes, we arrange full TM44 inspections for commercial
                    properties in {data.name}. If your building's combined
                    cooling capacity exceeds 12kW, we ensure you meet your
                    5-year compliance obligations.
                  </p>
                </div>
              </div>
            </div>

            <div data-id="element-1251">
              <h3 className="text-xl font-bold text-primary-dark mb-6 border-b border-primary-dark/10 pb-2" data-id="element-1252">
                Suitability & Sector
              </h3>
              <div className="space-y-6" data-id="element-1253">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1254">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1255">
                    Do you work with landlords and FM companies in {data.name}?
                  </h4>
                  <p className="text-gray-600" data-id="element-1256">
                    Yes, FM companies and commercial landlords form the core of
                    our client base in {data.name}. We provide reliable,
                    manufacturer-level support and multi-year maintenance
                    contracts tailored to your portfolio.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1257">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1258">
                    Can you help with AC in schools and healthcare settings in{' '}
                    {data.name}?
                  </h4>
                  <p className="text-gray-600" data-id="element-1259">
                    Absolutely. We have extensive experience providing
                    compliant, quiet, and highly efficient HVAC and ventilation
                    solutions for educational and healthcare facilities
                    throughout the region.
                  </p>
                </div>
              </div>
            </div>

            <div data-id="element-1260">
              <h3 className="text-xl font-bold text-primary-dark mb-6 border-b border-primary-dark/10 pb-2" data-id="element-1261">
                Process & Pricing
              </h3>
              <div className="space-y-6" data-id="element-1262">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1263">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1264">
                    How quickly can you attend a survey in {data.name}?
                  </h4>
                  <p className="text-gray-600" data-id="element-1265">
                    We can typically arrange a free commercial site survey in{' '}
                    {data.name} within 48 hours of your initial enquiry.
                  </p>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-primary-dark/5" data-id="element-1266">
                  <h4 className="font-bold text-primary-dark mb-2" data-id="element-1267">
                    How much does commercial AC installation cost in {data.name}
                    ?
                  </h4>
                  <p className="text-gray-600" data-id="element-1268">
                    Costs vary significantly based on building size, system
                    type, and specific requirements. We provide detailed,
                    transparent quotes following a comprehensive site survey and
                    heat-load calculation, ensuring you get the right system for
                    your budget.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}